import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, CheckIcon, ListBulletIcon, PlusIcon, CogIcon } from '@heroicons/react/24/outline';
import { NafList } from '../../types/nafList';
import { getNafLists } from '../../services/nafListService';
import { showErrorToast } from '../../utils/toastUtils';

interface NafListSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectLists: (nafCodes: string[]) => void;
  disabled?: boolean;
}

const NafListSelectionModal: React.FC<NafListSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectLists,
  disabled = false,
}) => {
  const [nafLists, setNafLists] = useState<NafList[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch NAF lists when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNafLists();
      setSelectedListIds([]); // Reset selection when modal opens
    }
  }, [isOpen]);

  const fetchNafLists = async () => {
    setIsLoading(true);
    try {
      const lists = await getNafLists();
      setNafLists(lists);
    } catch (error) {
      console.error('Error fetching NAF lists:', error);
      showErrorToast('Erreur lors du chargement des listes NAF', 'fetch-naf-lists-modal-error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleListToggle = (listId: number) => {
    setSelectedListIds(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleValidate = () => {
    // Collect all NAF codes from selected lists
    const selectedLists = nafLists.filter(list => selectedListIds.includes(list.id));
    const allNafCodes = selectedLists.reduce((acc, list) => {
      // Add codes that aren't already in the accumulator
      list.nafCodes.forEach(code => {
        if (!acc.includes(code)) {
          acc.push(code);
        }
      });
      return acc;
    }, [] as string[]);

    onSelectLists(allNafCodes);
    onClose();
  };

  const handleClose = () => {
    if (!disabled) {
      onClose();
    }
  };

  const selectedLists = nafLists.filter(list => selectedListIds.includes(list.id));
  const totalNafCodes = selectedLists.reduce((acc, list) => {
    list.nafCodes.forEach(code => {
      if (!acc.includes(code)) {
        acc.push(code);
      }
    });
    return acc;
  }, [] as string[]).length;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[1001]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Sélectionner des listes NAF prédéfinies
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={handleClose}
                    disabled={disabled}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {isLoading ? (
                  <div className="py-8">
                    <div className="animate-pulse space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-16 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </div>
                ) : nafLists.length === 0 ? (
                  <div className="text-center py-12">
                    <ListBulletIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune liste NAF disponible</h3>
                    <p className="mt-1 text-sm text-gray-500 mb-6">
                      Créez d'abord des listes NAF prédéfinies pour pouvoir les utiliser ici.
                    </p>
                    <Link
                      to="/listes-naf"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <CogIcon className="h-4 w-4 mr-2" />
                      Créer et gérer les listes NAF
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600">
                        Sélectionnez une ou plusieurs listes pour ajouter leurs codes NAF à votre recherche.
                      </p>
                    </div>

                    <div className="max-h-96 overflow-y-auto space-y-3 mb-6">
                      {nafLists.map((list) => {
                        const isSelected = selectedListIds.includes(list.id);
                        return (
                          <div
                            key={list.id}
                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => handleListToggle(list.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                                    isSelected 
                                      ? 'border-blue-500 bg-blue-500' 
                                      : 'border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <CheckIcon className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {list.name}
                                  </h4>
                                </div>
                                
                                {list.description && (
                                  <p className="text-xs text-gray-600 mt-1 ml-8">
                                    {list.description}
                                  </p>
                                )}
                                
                                <div className="flex items-center mt-2 ml-8">
                                  <ListBulletIcon className="h-4 w-4 text-gray-400 mr-1" />
                                  <span className="text-xs text-gray-500">
                                    {list.nafCodes.length} code{list.nafCodes.length > 1 ? 's' : ''} NAF
                                  </span>
                                </div>

                                {/* Preview of NAF codes */}
                                <div className="flex flex-wrap gap-1 mt-2 ml-8">
                                  {list.nafCodes.slice(0, 4).map((code) => (
                                    <span
                                      key={code}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                    >
                                      {code}
                                    </span>
                                  ))}
                                  {list.nafCodes.length > 4 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                      +{list.nafCodes.length - 4}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Selection summary */}
                    {selectedListIds.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                          <CheckIcon className="h-5 w-5 text-blue-500 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              {selectedListIds.length} liste{selectedListIds.length > 1 ? 's' : ''} sélectionnée{selectedListIds.length > 1 ? 's' : ''}
                            </p>
                            <p className="text-xs text-blue-700">
                              {totalNafCodes} code{totalNafCodes > 1 ? 's' : ''} NAF unique{totalNafCodes > 1 ? 's' : ''} seront ajouté{totalNafCodes > 1 ? 's' : ''} à votre recherche
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                      {/* Management button on the left */}
                      <Link
                        to="/listes-naf"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <CogIcon className="h-4 w-4 mr-2" />
                        Gérer les listes
                      </Link>

                      {/* Action buttons on the right */}
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={disabled}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          onClick={handleValidate}
                          disabled={disabled || selectedListIds.length === 0}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <PlusIcon className="h-4 w-4 mr-2 inline" />
                          Ajouter {totalNafCodes > 0 ? `${totalNafCodes} code${totalNafCodes > 1 ? 's' : ''}` : 'les codes'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NafListSelectionModal; 